import type { ParseKeys } from "i18next";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { CommandItem } from "@/components/ui/command";
import { getAutoDirection } from "@/lib/direction";

import {
  GLOBAL_SEARCH_BADGE_KEYS,
  GLOBAL_SEARCH_ROW_CLASS_NAME,
} from "./constants";
import { GlobalSearchItemLogo } from "./GlobalSearchItemLogo";
import type { IGlobalSearchItemRowProps } from "./types";
import { getGlobalSearchItemSubtitle } from "./utils/get-global-search-item-logo";

// NOTE: One result row, styled like the chat rows it sits beside (NSAI-6853:
// title on the left, its context muted on the right). Titles are
// user content in any script, so each line picks its own direction.
const GlobalSearchItemRowComponent = ({
  item,
  onSelectItem,
}: IGlobalSearchItemRowProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const subtitle = useMemo(() => getGlobalSearchItemSubtitle(item), [item]);
  const badgeKey: ParseKeys<"globalSearch"> | undefined =
    item.params.badge === undefined
      ? undefined
      : GLOBAL_SEARCH_BADGE_KEYS[item.params.badge];
  const itemKey = `${item.type}:${item.id}`;

  const handleSelect = useCallback((): void => {
    onSelectItem(item);
  }, [item, onSelectItem]);

  return (
    <CommandItem
      value={itemKey}
      onSelect={handleSelect}
      className={GLOBAL_SEARCH_ROW_CLASS_NAME}
      data-testid={`global-search-item-${itemKey}`}
    >
      <GlobalSearchItemLogo item={item} />
      {/* NOTE: NSAI-6853 — side by side on wide screens, stacked on narrow
          ones, so a long Russian or Arabic title is never squeezed to a few
          letters by its subtitle. Both lines truncate; `title` keeps the full
          text one hover away. */}
      <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-center sm:gap-3">
        <p
          dir={getAutoDirection({ content: item.title })}
          title={item.title}
          className="min-w-0 truncate text-start text-sm text-foreground sm:flex-1"
        >
          {item.title}
        </p>
        {subtitle && (
          <p
            dir={getAutoDirection({ content: subtitle })}
            title={subtitle}
            className="min-w-0 truncate text-start text-xs text-foreground-secondary sm:max-w-[40%] sm:shrink-0 sm:text-end"
          >
            {subtitle}
          </p>
        )}
      </div>
      {badgeKey && (
        <Badge
          variant="secondary"
          className="max-w-24 shrink-0 truncate rounded-full border-0 bg-glass-fill text-[10px] font-medium text-foreground-secondary"
        >
          {t(badgeKey)}
        </Badge>
      )}
    </CommandItem>
  );
};

export const GlobalSearchItemRow = memo(GlobalSearchItemRowComponent);
